import { useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { MaintenanceListTableDisplay } from '@/components/sections/service/maintenance-list/table';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { formatPrice } from '@/components/shared/utility/format';
import { styled } from '@/stitches.config';
import { RegularMaintenaceSchedule } from '@/types/services/contentstack/services/maintenance-service';

interface TableBriefHeaderNewProps {
  dataTest: string;
  selectedIndex: number;
  selectedMaintenanceSchedule?: RegularMaintenaceSchedule;
  maintenanceSchedules?: RegularMaintenaceSchedule[];
  tableDisplay?: MaintenanceListTableDisplay;
  onChangeSchedule?: (schedule: RegularMaintenaceSchedule) => void;
}

const ScheduleSelectBox = styled(Box, {
  backgroundColor: '$gray200',
  border: '1px solid',
  borderColor: '$gray900',
  color: '$gray900',
  transition: 'backgroundColor 600ms var(--transition-easing)',
  '@hover': {
    '&:hover': {
      backgroundColor: '$gray900',
      color: '$white',
    },
  },
  variants: {
    active: {
      true: {
        pe: 'none',
        backgroundColor: '$primary',
        borderColor: '$primary',
        color: '$white',
      },
    },
  },
});

const NavigationButton = styled(Box, {
  transition: 'opacity 1s var(--transition-easing)',
  aspectRatio: '1 / 1',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '$size-full',
    height: '$size-full',
    pointerEvents: 'none',
  },
  '&.button-prev': {
    '&::before': {
      left: 0,
      linearGradient: '90deg, $white 0%, rgba(255, 255, 255, 0) 100%',
    },
  },
  '&.button-next': {
    '&::before': {
      right: 0,
      linearGradient: '270deg, $white 0%, rgba(255, 255, 255, 0) 100%',
    },
  },
  '&.swiper-button-disabled': {
    pointerEvents: 'none',
    opacity: '0',
    visibility: 'hidden',
  },

  '@xl': {
    aspectRatio: 'initial',

    '&.button-prev': {
      '&::before': {
        linearGradient: '90deg, $white 5%, rgba(255, 255, 255, 0) 100%',
      },
    },
    '&.button-next': {
      '&::before': {
        linearGradient: '270deg, $white 5%, rgba(255, 255, 255, 0) 100%',
      },
    },
  },
});

export const TableBriefHeaderNew = ({
  maintenanceSchedules,
  selectedIndex,
  tableDisplay,
  onChangeSchedule,
}: TableBriefHeaderNewProps) => {
  const BOX_REF = useRef<HTMLDivElement>(null);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);

  const onClickSchedule = (index: number) => {
    onChangeSchedule &&
      maintenanceSchedules &&
      onChangeSchedule(maintenanceSchedules[index]);
  };

  useEffect(() => {
    if (!BOX_REF.current) return;

    const onScroll = () => {
      if (!BOX_REF.current) return;

      const { top } = BOX_REF.current.getBoundingClientRect();
      setIsSticky(top <= 0.5);
    };

    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    if (!swiper || tableDisplay === 'full') return;

    const id = setTimeout(() => {
      swiper.slideTo(selectedIndex);
    }, 100);

    return () => {
      clearTimeout(id);
    };
  }, [swiper, selectedIndex, tableDisplay]);

  return (
    <Box
      ref={BOX_REF}
      justifyContent="center"
      alignItems="center"
      className="group"
      position="sticky"
      top="sticky-top"
      width="full"
      height="auto"
      paddingTop={{ '@initial': 2, '@xl': 6 }}
      paddingBottom="2"
      zIndex="2"
      backgroundColor="white"
      overflow="hidden"
      boxShadow={isSticky ? '200' : 'none'}
      css={{
        '& .swiper': {
          overflow: 'initial',
        },
        '& .swiper-slide': {
          width: 'auto !important',
        },
      }}
      test={{ dataTest: 'header_schedule' }}
    >
      <Container
        size="sm"
        css={{
          position: 'relative',
        }}
      >
        <Swiper
          modules={[Navigation]}
          slidesPerView="auto"
          spaceBetween={12}
          threshold={10}
          allowTouchMove
          navigation={{
            prevEl: '.button-prev',
            nextEl: '.button-next',
          }}
          breakpoints={{
            1280: {
              spaceBetween: 16,
            },
          }}
          centeredSlides
          centeredSlidesBounds
          // initialSlide={selectedIndex - 1}
          onSwiper={setSwiper}
        >
          {!!maintenanceSchedules?.length &&
            maintenanceSchedules.map((o, i) => (
              <SwiperSlide key={i}>
                <ScheduleSelectBox
                  paddingY="0-5"
                  paddingX={{ '@initial': 4, '@xl': 10 }}
                  rounded="1-5"
                  test={{
                    dataTest: `schedule_${i}`,
                  }}
                  active={selectedIndex === i}
                  cursor="pointer"
                  onClick={() => onClickSchedule(i)}
                  textAlign="center"
                >
                  <Text
                    size={{ '@initial': 'text-body-2', '@xl': 'text-body-1' }}
                    font="bold"
                    variant="text-current"
                  >
                    {`ระยะ ${o.month} เดือน`}
                  </Text>
                  <Text
                    size={{ '@initial': 'text-body-2', '@xl': 'text-body-1' }}
                    dangerouslySetInnerHTML={{
                      __html: `หรือ <strong>${formatPrice(
                        o.mileage,
                        false
                      )} กม.</strong>`,
                    }}
                    variant="text-current"
                  />
                </ScheduleSelectBox>
              </SwiperSlide>
            ))}
        </Swiper>
      </Container>
      <NavigationButton
        position="absolute"
        top="0"
        left="0"
        zIndex="1"
        className="button-prev"
        width={{ '@initial': 'auto', '@xl': '18' }}
        height={{ '@initial': 'full', '@xl': '22' }}
        paddingTop={{ '@xl': '5' }}
        display="flex"
        alignItems="center"
        justifyContent={{ '@initial': 'center', '@xl': 'end' }}
        test={{
          dataTest: 'tags_button_previous',
        }}
      >
        <ButtonIcon
          icon="arrow-back"
          size="sm"
          variant="button-icon-color-gray-dark"
        />
      </NavigationButton>
      <NavigationButton
        position="absolute"
        top="0"
        right="0"
        zIndex="1"
        className="button-next"
        width={{ '@initial': 'auto', '@xl': '18' }}
        height={{ '@initial': 'full', '@xl': '22' }}
        paddingTop={{ '@xl': '5' }}
        display="flex"
        alignItems="center"
        justifyContent={{ '@initial': 'center', '@xl': 'start' }}
        test={{
          dataTest: 'tags_button_next',
        }}
      >
        <ButtonIcon
          icon="arrow-forward"
          size="sm"
          variant="button-icon-color-gray-dark"
        />
      </NavigationButton>
    </Box>
  );
};
